import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import PropTypes, { number } from "prop-types";
import { withRouter } from '../../common/withRouter';
import { 
  Loader,  
  Notification,
  ButtonOBSOLETED,
  Message,
  Icon
 } from "../../web.common";
import { DisplayDate, DisplayGuid } from "../items";
import { LayoutContext } from "../layout/siteLayout";
import PageTop from "../layout/PageTop";
import withApi from "../higherOrderComponents/withApi";
import AriaModal from 'react-aria-modal';

const OverviewInternal = (props) => {
  const [loadedData, setData] = useState(null);
  const [notification, setNotification] = useState(null);
  const [modalContent, setModalContent] = useState(false);

  useEffect(() => {
    async function loadFromApi() {
      const data = await props.api.overview.getOverview();
      setData(data);
    }

    if (!loadedData) {
      loadFromApi();
    }
  });

  const openModal = (comment, credentials) => {
    setModalContent(
        <AriaModal
          titleText='Show comments and credentials'
          mounted
          verticallyCenter={false}
          focusDialog
          getApplicationNode={() => document.getElementById('app')}
          dialogClass='dialog-size-normal'
        >
          <div>
            <p>
              {comment != null && comment.split('\n').map((line, index) => <span key={index}>{line}<br /></span>)}
            </p>
            <p>
              {credentials != null && credentials.split('\n').map((line, index) => <span key={index}>{line}<br /></span>)}
            </p>
          </div>
         
          <ButtonOBSOLETED
            buttonColor='gray'
            size='small'
            onClick={() => {setModalContent(false)}}
            disabled={false}
            buttonContent='Back'
            ariaLabel='return'
          >
            Back
          </ButtonOBSOLETED>
        </AriaModal>
      
    )
  }

  const mute = async (signupId, isBooking) => {
    const regexExp =
      /^[0-9a-fA-F]{8}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{12}$/gi;

    const isGuid = regexExp.test(signupId);

    if (isGuid) {
      const response = await props.api.overview.mute(signupId, isBooking);
      if (response.success) {
        const message = `${isBooking ? 'Booking' : 'Cancellation'} muted successfully`;
        setNotification(new Message('success', message));
        const data = await props.api.overview.getOverview();
        setData(data);
      } else {
        setNotification(new Message('error', 'Unable to mute this signup.')); 
      }
    }
  };

  async function retryHandler(signup, isBooking){
    if(confirm(`Do you want to retry this ${isBooking ? 'booking' : 'cancellation'}?`)){
      const weirdCase = isBooking ? 
        (signup.bookStatus == 'Success') : 
        (signup.cancellationStatus == 'Success');
      const weirdCaseMessage = `the ${isBooking ? 'booking' : 'cancellation'} has already been successful, do you want to retry anyway?`;
      
      const normalCase = ((isBooking == true && signup.bookStatus == 'Failure') || 
                          (isBooking == false && signup.cancellationStatus == 'Failure'));
      
      const unusualCaseMessage = `Do you really want to put in the queue this ${isBooking ? 'booking' : 'cancellation'}?`;
      
      if(weirdCase)
      {
        if(!confirm(weirdCaseMessage)){
          return;
        }
      } else if (!normalCase){
        if(!confirm(unusualCaseMessage)){
          return;
        }
      }
      
      let result = await props.api.overview.retry(signup.id, isBooking, false);
      // Refused because the last attempt is still Pending: offer a forced retry for a stuck one.
      if (result.canForce && confirm(`${result.message}\n\nRetry anyway? Only do this if the attempt is stuck: if it is still running, the provider gets the request twice.`)) {
        result = await props.api.overview.retry(signup.id, isBooking, true);
      }
      const messageType = result.success ? 'success' : 'error';
      const message = result.success ?
        'signup has been put back in the queue.' :
        (result.message || 'unable to put the signup back in the queue.');

      setNotification(new Message(messageType, message));
    }
  }

  const editComment = async (signupId, comment) => {
    const commentResponse = prompt("Edit Comment", comment);

    if (commentResponse != null) {
      const response = await props.api.overview.editComment(
        signupId,
        commentResponse
      );
      
      if (response.success) {
        const data = await props.api.overview.getOverview();
        setData(data);
        setNotification(new Message("success", "the comment has been changed"));
      } else {
        setNotification(new Message("error", "Unable to change the comment."));
      }
    }
  }

  const sendConfirmation = async (signupId, isBooking) => {
    const regexExp =
      /^[0-9a-fA-F]{8}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{12}$/gi;

    const isGuid = regexExp.test(signupId);

    if (isGuid) {
      const response = await props.api.overview.sendConfirmation(
        signupId,
        isBooking
      );
      if (response.success) {
        const data = await props.api.overview.getOverview();
        setData(data);
        setNotification(new Message('success', 'confirmation has been sent correctly'));
      }else{
        setNotification(new Message('error', 'unable to confirm this signup'));
      }
    }
  };

  if (loadedData == null) {
    return (
      <div>
        <br />
        <br />
        <Loader />
      </div>
    );
  }

  const header = [
    {
      name: "Id",
      label: "Id",
      render: ({ id }) => <DisplayGuid baseAnchor={"signup"}>{id}</DisplayGuid>,
    },
    {
      name: "Facility",
      label: "Facility",
      render: ({ facilityName, provider }) => (
        <span>{provider || facilityName}</span>
      ),
    },
    {
      name: "Sport",
      label: "Sport",
      render: ({ sport }) => <span>{sport}</span>,
    },
    {
      name: "Title",
      label: "Title",
      render: ({ title }) => <span>{title}</span>,
    },
    {
      name: "UserName",
      label: "UserName",
      breakWord: true,
      render: ({ userName }) => <span>{userName}</span>,
    },
    {
      name: "SignupDateCet",
      label: "SignupDate (CET)",
      render: ({ signupDateCet }) => (
        <DisplayDate breakTime>{signupDateCet}</DisplayDate>
      ),
    },
    {
      name: "ActivityDateCet",
      label: "ActivityDate (CET)",
      render: ({ activityDateCet }) => (
        <DisplayDate breakTime>{activityDateCet}</DisplayDate>
      ),
    },
    {
      name: "Status",
      label: "Result",
      render: ({ bookStatus, cancellationStatus }, isBooking) =>
        <span>
          {isBooking ? camelToWords(bookStatus) : camelToWords(cancellationStatus)}
        </span>,
    },
    {
      name: "IsBundle",
      label: "IsBundle",
      render: ({ isBundle }) => <span>{isBundle === true ? "true" : "false"}</span>,
    },
    {
      name: "Secret",
      label: "Secret",
      render: ({ secretCode }) => <span>{secretCode}</span>,
    },
    {
      name: "Comment",
      label: "Comment",
      render: ({ signupComment }) => <span>{signupComment}</span>,
    },
    {
      name: "Actions",
      label: "Actions",
      render: (signup, isSignup) => (
        <div className="overview-actions">
          <div className="overview-actions__row">
            <button onClick={() => retryHandler(signup, isSignup)}>retry</button>
            <button onClick={() => mute(signup.id, isSignup)}>mute</button>
          </div>
          <div className="overview-actions__row">
            <button onClick={() => sendConfirmation(signup.id, isSignup)}>
              confirm
            </button>
            <button
              onClick={() => openModal(signup.comment, signup.credentials)}>
              help
            </button>
          </div>
          <div className="overview-actions__row">
            <button onClick={() => editComment(signup.id, signup.signupComment)}>
              <Icon className='true' iconName='edit' />edit
            </button>
          </div>
        </div>
      ),
    },
  ];

  return (
    <LayoutContext.Consumer>
      {() => (
        <React.Fragment>
         <Notification newMessage={notification} />
         {modalContent}
          <PageTop
            documentTitle="Overview page"
            documentDescription="Overview page"
          />
          <h2 className="main-page-header">Overview</h2>
          <h4>Bookings</h4>
          <article className="data-table-wrap">
            <table className="data-lists component-data-table cell-content-align-top">
              <thead>
                <tr>
                  {header.map((property) => (
                    <th>
                      {typeof property === "object" ? property.label : property}
                    </th>
                  ))}
                </tr>
              </thead>
              {loadedData &&
                loadedData.data
                  .filter(({ showInSignups }) => showInSignups)
                  .map((row) => (
                    <tbody>
                      <tr className="hover-listview ">
                        {header.map((property) => (
                          <td>
                            <strong className="table-cell-label">
                              {typeof property === "object"
                                ? property.label
                                : property}
                            </strong>
                            {typeof property === "object" ? (
                              property.render(row, true)
                            ) : property === "Status" ? (
                              <span>{camelToWords(row[property])}</span>
                            ) : (
                              <span>{row[property]}</span>
                            )}
                          </td>
                        ))}
                      </tr>
                    </tbody>
                  ))}
            </table>
          </article>
          <br />
          <hr />
          <br />
          <h4>Cancellations</h4>
          <article className="data-table-wrap">
            <table className="data-lists component-data-table cell-content-align-top">
              <thead>
                <tr>
                  {header.map((property) => (
                    <th>
                      {typeof property === "object" ? property.label : property}
                    </th>
                  ))}
                </tr>
              </thead>
              {loadedData &&
                loadedData.data
                  .filter(({ showInCancellation }) => showInCancellation)
                  .map((row) => (
                    <tbody>
                      <tr className="hover-listview ">
                        {header.map((property) => (
                          <td
                            style={
                              property.breakWord
                                ? { overflowWrap: "anywhere" }
                                : {}
                            }
                          >
                            <strong className="table-cell-label">
                              {typeof property === "object"
                                ? property.label
                                : property}
                            </strong>
                            {typeof property === "object" ? (
                              property.render(row)
                            ) : property === "Status" ? (
                              <span>{camelToWords(row[property])}</span>
                            ) : (
                              <span>{row[property]}</span>
                            )}
                          </td>
                        ))}
                      </tr>
                    </tbody>
                  ))}
            </table>
          </article>
        </React.Fragment>
      )}
    </LayoutContext.Consumer>
  );
};

function camelToWords(text) {
  if (text == null || text == undefined) {
    text = "null";
  }
  const result = text.replace(/([A-Z])/g, " $1");
  return result.charAt(0).toUpperCase() + result.slice(1);
}

const Overview = withRouter(
  withApi(
    connect(
      ({ user, categories }) => ({ isLoggedIn: !!user, categories }),
      {}
    )(OverviewInternal)
  )
);

Overview.requiresLogin = true;
Overview.propTypes = { isLoggedIn: PropTypes.bool };

export default Overview;
